/**
 * EVENTS PAGE
 * 
 * Community calendar for indie sales, restocks, releases, and more.
 * Features:
 * - Toggle between List and Calendar view
 * - Filter by event type
 * - Brand owners can add events
 * - "Add to Calendar" export
 * 
 * Location: src/pages/EventsPage.tsx
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Calendar,
  List,
  Plus,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Bell
} from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { auth } from '../firebase/config';
import { 
  getUpcomingEvents, 
  getEventsForMonth,
  getUserBrands,
  deleteEvent
} from '../services/eventService';
import { 
  EVENT_TYPE_CONFIG, 
  type CalendarEvent, 
  type EventType,
  type CalendarDay 
} from '../types/event';
import EventCard from '../components/events/EventCard';
import AddEventModal from '../components/events/AddEventModal';
import EventReminderModal from '../components/events/EventReminderModal';
import EventDetailModal from '../components/events/EventDetailModal';
import { PageHeader } from '../components/common';

type ViewMode = 'list' | 'calendar';

export default function EventsPage() {
  // Auth state (using Firebase directly)
  const [user, setUser] = useState<User | null>(null);
  const [, setAuthLoading] = useState(true);
  
  // Listen for auth changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);
  
  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Data
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [selectedTypes, setSelectedTypes] = useState<EventType[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Brand owner check
  const [userBrands, setUserBrands] = useState<Array<{ id: string; name: string }>>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Reminder modal
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedEventForReminder, setSelectedEventForReminder] = useState<CalendarEvent | null>(null);
  
  // Detail modal (for calendar view clicks)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEventForDetail, setSelectedEventForDetail] = useState<CalendarEvent | null>(null);
  
  // Editing event (reuses AddEventModal)
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  
  const handleSetReminder = (event: CalendarEvent) => {
    setSelectedEventForReminder(event);
    setShowReminderModal(true);
  };
  
  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEventForDetail(event);
    setShowDetailModal(true);
  };
  
  // Check if current user can manage an event (is brand owner)
  const canUserManageEvent = (event: CalendarEvent): boolean => {
    if (!user) return false;
    return userBrands.some(brand => brand.id === event.brandId);
  };
  
  // Handle editing an event
  const handleEditEvent = (event: CalendarEvent) => {
    setEditingEvent(event);
    setShowAddModal(true);
  };
  
  // Handle deleting an event
  const handleDeleteEvent = async (event: CalendarEvent) => {
    if (!event.id) return;
    
    try {
      await deleteEvent(event.id);
      // Remove from local state
      setEvents(prev => prev.filter(e => e.id !== event.id));
    } catch (err) {
      console.error('Error deleting event:', err);
      alert('Failed to delete event. Please try again.');
    }
  };

  // Check if user owns any brands (can post events)
  useEffect(() => {
    async function checkBrands() {
      if (!user) {
        setUserBrands([]);
        return;
      }
      
      try {
        const brands = await getUserBrands(user.uid, user.email || '');
        setUserBrands(brands);
      } catch (err) {
        console.error('Error checking brands:', err);
      }
    }
    
    checkBrands();
  }, [user]);

  // Load events
  useEffect(() => {
    async function loadEvents() {
      setLoading(true);
      setError('');
      
      try {
        let fetchedEvents: CalendarEvent[];
        
        if (viewMode === 'calendar') {
          // Load events for the current month
          fetchedEvents = await getEventsForMonth(
            currentMonth.getFullYear(),
            currentMonth.getMonth()
          );
        } else {
          // Load upcoming events
          fetchedEvents = await getUpcomingEvents(
            selectedTypes.length > 0 ? { eventTypes: selectedTypes } : undefined,
            50
          );
        }
        
        setEvents(fetchedEvents);
      } catch (err) {
        console.error('Error loading events:', err);
        setError('Failed to load events. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    
    loadEvents();
  }, [viewMode, currentMonth, selectedTypes]);

  // Filter events by type for list view
  const filteredEvents = useMemo(() => {
    if (selectedTypes.length === 0) return events;
    return events.filter(e => selectedTypes.includes(e.eventType));
  }, [events, selectedTypes]);

  // Generate calendar grid
  const calendarDays = useMemo((): CalendarDay[] => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Today
    const today = new Date();
    const todayStr = today.toDateString();
    
    const days: CalendarDay[] = [];
    
    // Previous month's trailing days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonth.getDate() - i);
      
      // Check for multi-day events that span into these days
      const dayEvents = events.filter(event => {
        const eventStart = event.startDate.toDate();
        const eventEnd = event.endDate ? event.endDate.toDate() : eventStart;
        
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        
        const eventStartDay = new Date(eventStart);
        eventStartDay.setHours(0, 0, 0, 0);
        
        const eventEndDay = new Date(eventEnd);
        eventEndDay.setHours(23, 59, 59, 999);
        
        return dayStart >= eventStartDay && dayStart <= eventEndDay;
      });
      
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        events: dayEvents
      });
    }
    
    // Current month's days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      const dateStr = date.toDateString();
      
      // Find events for this day - check if day falls within event's date range
      const dayEvents = events.filter(event => {
        const eventStart = event.startDate.toDate();
        const eventEnd = event.endDate ? event.endDate.toDate() : eventStart;
        
        // Normalize dates to midnight for comparison
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        
        const eventStartDay = new Date(eventStart);
        eventStartDay.setHours(0, 0, 0, 0);
        
        const eventEndDay = new Date(eventEnd);
        eventEndDay.setHours(23, 59, 59, 999);
        
        // Check if this day falls within the event's date range
        return dayStart >= eventStartDay && dayStart <= eventEndDay;
      });
      
      days.push({
        date,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        events: dayEvents
      });
    }
    
    // Next month's leading days (fill to 42 = 6 rows)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      
      // Check for multi-day events that span into these days
      const dayEvents = events.filter(event => {
        const eventStart = event.startDate.toDate();
        const eventEnd = event.endDate ? event.endDate.toDate() : eventStart;
        
        const dayStart = new Date(date);
        dayStart.setHours(0, 0, 0, 0);
        
        const eventStartDay = new Date(eventStart);
        eventStartDay.setHours(0, 0, 0, 0);
        
        const eventEndDay = new Date(eventEnd);
        eventEndDay.setHours(23, 59, 59, 999);
        
        return dayStart >= eventStartDay && dayStart <= eventEndDay;
      });
      
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        events: dayEvents
      });
    }
    
    return days;
  }, [currentMonth, events]);

  // Navigation
  const goToPrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Toggle event type filter
  const toggleTypeFilter = (type: EventType) => {
    setSelectedTypes(prev => 
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Refresh events after adding
  const handleEventAdded = () => {
    // Re-fetch events
    setLoading(true);
    if (viewMode === 'calendar') {
      getEventsForMonth(currentMonth.getFullYear(), currentMonth.getMonth())
        .then(setEvents)
        .finally(() => setLoading(false));
    } else {
      getUpcomingEvents(
        selectedTypes.length > 0 ? { eventTypes: selectedTypes } : undefined,
        50
      )
        .then(setEvents)
        .finally(() => setLoading(false));
    }
  };

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-6xl mx-auto">
        {/* HEADER */}
        <PageHeader
          icon={Calendar}
          title="Events Calendar"
          subtitle="Never miss a sale, restock, or release again!"
          className="mb-6"
        />

        {/* CONTROLS BAR */}
        <div className="glass-card p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* View toggle */}
            <div className="flex items-center gap-2 bg-slate-800/60 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-default ${
                  viewMode === 'list'
                    ? 'bg-pink-500 text-white'
                    : 'text-glass-secondary hover:text-glass'
                }`}
              >
                <List className="icon-sm" />
                <span className="hidden sm:inline">List</span>
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-default ${
                  viewMode === 'calendar'
                    ? 'bg-pink-500 text-white'
                    : 'text-glass-secondary hover:text-glass'
                }`}
              >
                <Calendar className="icon-sm" />
                <span className="hidden sm:inline">Calendar</span>
              </button>
            </div>

            {/* Calendar navigation (only in calendar view) */}
            {viewMode === 'calendar' && (
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevMonth}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-default"
                >
                  <ChevronLeft className="icon-base text-glass-secondary" />
                </button>
                <button
                  onClick={goToToday}
                  className="px-3 py-1 text-sm text-glass-secondary hover:text-glass hover:bg-slate-700 rounded-lg transition-default"
                >
                  Today
                </button>
                <h2 className="text-lg font-bold text-glass min-w-[150px] text-center">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-default"
                >
                  <ChevronRight className="icon-base text-glass-secondary" />
                </button>
              </div>
            )}

            {/* Filter and Add buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-default ${
                  showFilters || selectedTypes.length > 0
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/50'
                    : 'bg-slate-700 text-glass-secondary hover:text-glass'
                }`}
              >
                <Filter className="icon-sm" />
                <span className="hidden sm:inline">Filter</span>
                {selectedTypes.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-purple-500 text-white text-xs rounded-full">
                    {selectedTypes.length}
                  </span>
                )}
              </button>
              
              {/* Add Event button (only for brand owners) */}
              {userBrands.length > 0 && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Plus className="icon-sm" />
                  <span className="hidden sm:inline">Add Event</span>
                </button>
              )}
            </div>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-700">
              <p className="text-sm text-glass-secondary mb-3">Filter by event type:</p>
              <div className="flex flex-wrap gap-2">
                {(Object.entries(EVENT_TYPE_CONFIG) as [EventType, typeof EVENT_TYPE_CONFIG[EventType]][]).map(([type, config]) => (
                  <button
                    key={type}
                    onClick={() => toggleTypeFilter(type)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-default ${
                      selectedTypes.includes(type)
                        ? `${config.color} text-white`
                        : 'bg-slate-700 text-glass-secondary hover:text-glass'
                    }`}
                  >
                    <span>{config.emoji}</span>
                    <span>{config.label}</span>
                    {selectedTypes.includes(type) && (
                      <X className="w-3 h-3" />
                    )}
                  </button>
                ))}
                
                {selectedTypes.length > 0 && (
                  <button
                    onClick={() => setSelectedTypes([])}
                    className="text-sm text-glass-muted hover:text-glass-secondary ml-2"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ERROR STATE - Only for list view */}
        {error && viewMode === 'list' && (
          <div className="glass-card p-6 text-center mb-6">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-secondary"
            >
              Try Again
            </button>
          </div>
        )}

        {/* LOADING STATE - Only for list view */}
        {loading && viewMode === 'list' && (
          <div className="glass-card p-12 text-center">
            <div className="animate-spin w-10 h-10 border-2 border-pink-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-glass-secondary">Loading events...</p>
          </div>
        )}

        {/* LIST VIEW */}
        {!loading && !error && viewMode === 'list' && (
          <div className="space-y-4">
            {filteredEvents.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-700 flex items-center justify-center mx-auto mb-4">
                  <Calendar className="icon-lg text-glass-secondary" />
                </div>
                <h3 className="text-lg font-bold text-glass mb-2">No Upcoming Events</h3>
                <p className="text-glass-secondary">
                  {selectedTypes.length > 0
                    ? 'No events match your filters. Try removing some filters.'
                    : 'Check back soon for sales, restocks, and releases!'}
                </p>
                {userBrands.length > 0 && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="btn-primary mt-4"
                  >
                    <Plus className="icon-sm mr-2" />
                    Add the first event
                  </button>
                )}
              </div>
            ) : (
              filteredEvents.map(event => (
                <EventCard 
                  key={event.id} 
                  event={event} 
                  onSetReminder={handleSetReminder}
                  onClick={handleEventClick}
                />
              ))
            )}
          </div>
        )}

        {/* CALENDAR VIEW - Always show the grid */}
        {viewMode === 'calendar' && (
          <div className="glass-card p-4 sm:p-6">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2 text-center text-sm text-glass-muted mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="font-medium py-2">
                  <span className="hidden sm:inline">{day}</span>
                  <span className="sm:hidden">{day.charAt(0)}</span>
                </div>
              ))}
            </div>
            
            {/* Loading overlay for calendar */}
            {loading && (
              <div className="text-center py-4 text-glass-secondary">
                <div className="animate-spin w-6 h-6 border-2 border-pink-500 border-t-transparent rounded-full mx-auto mb-2" />
                Loading events...
              </div>
            )}
            
            {/* Error message for calendar */}
            {error && !loading && (
              <div className="text-center py-4 mb-4 bg-red-500/10 rounded-lg">
                <p className="text-red-400 text-sm mb-2">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm text-pink-400 hover:text-pink-300"
                >
                  Try Again
                </button>
              </div>
            )}
            
            {/* Calendar grid - always visible */}
            <div className="grid grid-cols-7 gap-1 sm:gap-2">
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  className={`min-h-[80px] sm:min-h-[100px] rounded-lg p-1 sm:p-2 transition-all hover:bg-slate-700/60 ${
                    day.isCurrentMonth
                      ? 'bg-slate-800/60'
                      : 'bg-slate-900/40 opacity-60'
                  } ${
                    day.isToday
                      ? 'ring-2 ring-pink-500 bg-pink-500/10'
                      : ''
                  }`}
                >
                  {/* Day number */}
                  <div className={`text-sm font-medium mb-1 ${
                    day.isCurrentMonth ? 'text-glass' : 'text-glass-muted'
                  } ${
                    day.isToday ? 'text-pink-400 font-bold' : ''
                  }`}>
                    {day.date.getDate()}
                  </div>
                  
                  {/* Event dots/cards */}
                  <div className="space-y-1">
                    {day.events.slice(0, 3).map(event => (
                      <EventCard key={event.id} event={event} compact onClick={handleEventClick} />
                    ))}
                    {day.events.length > 3 && (
                      <div className="text-xs text-glass-muted text-center">
                        +{day.events.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Empty state message */}
            {!loading && !error && events.length === 0 && (
              <div className="text-center py-6 mt-4 border-t border-slate-700">
                <p className="text-glass-secondary mb-2">No events this month</p>
                {userBrands.length > 0 && (
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="text-pink-400 hover:text-pink-300 text-sm flex items-center gap-1 mx-auto"
                  >
                    <Plus className="icon-sm" />
                    Add the first event
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* LEGEND */}
        <div className="glass-card p-4 mt-6">
          <h3 className="text-sm font-medium text-glass-secondary mb-3">Event Types</h3>
          <div className="flex flex-wrap gap-4">
            {(Object.entries(EVENT_TYPE_CONFIG) as [EventType, typeof EVENT_TYPE_CONFIG[EventType]][]).map(([type, config]) => (
              <div key={type} className="flex items-center gap-2 text-sm">
                <span className={`w-3 h-3 rounded-full ${config.color}`} />
                <span className="text-glass-secondary">{config.emoji} {config.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA for non-brand owners */}
        {user && userBrands.length === 0 && (
          <div className="glass-card p-6 mt-6 text-center">
            <Bell className="icon-lg text-purple-400 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-glass mb-2">Want to post events?</h3>
            <p className="text-glass-secondary mb-4">
              Claim your brand page to start sharing sales, restocks, and releases with the community!
            </p>
            <a href="/brands" className="btn-primary inline-block">
              Browse & Claim Brands
            </a>
          </div>
        )}
      </div>

      {/* Add/Edit Event Modal */}
      <AddEventModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingEvent(null);
        }}
        onSuccess={handleEventAdded}
        editingEvent={editingEvent}
      />
      
      {/* Event Reminder Modal */}
      {selectedEventForReminder && (
        <EventReminderModal
          isOpen={showReminderModal}
          onClose={() => {
            setShowReminderModal(false);
            setSelectedEventForReminder(null);
          }}
          event={selectedEventForReminder}
        />
      )}
      
      {/* Event Detail Modal (for calendar clicks) */}
      <EventDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedEventForDetail(null);
        }}
        event={selectedEventForDetail}
        onSetReminder={handleSetReminder}
        canManageEvent={selectedEventForDetail ? canUserManageEvent(selectedEventForDetail) : false}
        onEdit={handleEditEvent}
        onDelete={handleDeleteEvent}
      />
    </div>
  );
}
